import { useState } from 'react';
import { testimonials } from '../../../mocks/testimonials';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const StarRating = ({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className={size === 'md' ? 'w-4 h-4 flex items-center justify-center' : 'w-3 h-3 flex items-center justify-center'}>
        <i className={`ri-star-fill text-brand-gold ${size === 'md' ? 'text-sm' : 'text-xs'}`} />
      </div>
    ))}
  </div>
);

export default function Testimonials() {
  const [active, setActive] = useState(0);

  return (
    <section className="bg-brand-dark py-24 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/5 rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-green/5 rounded-full -translate-x-1/2 translate-y-1/2" />
      {/* Top diagonal */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-4">
        {/* Header */}
        <RevealOnScroll>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6">
            <div>
              <span className="text-brand-gold text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
                <span className="w-8 h-px bg-brand-gold inline-block" />
                Client Testimonials
              </span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mt-4 leading-tight">
                What Chicago Homeowners<br />
                <span className="text-brand-gold italic">&amp; Business Owners Say</span>
              </h2>
            </div>
            {/* Rating badge */}
            <div className="flex items-center gap-5 bg-white/8 border border-white/15 rounded-2xl px-7 py-5">
              <div className="text-center">
                <div className="font-display text-5xl font-bold text-white leading-none">5.0</div>
                <StarRating rating={5} size="md" />
                <div className="text-gray-400 text-xs mt-1.5">Average Rating</div>
              </div>
              <div className="w-px h-14 bg-white/10" />
              <div>
                <div className="text-white text-sm font-bold">Consistently 5-Star</div>
                <div className="text-gray-400 text-xs mt-1">Google · Yelp · HomeAdvisor</div>
                <div className="flex items-center gap-1 mt-2">
                  <i className="ri-verified-badge-fill text-brand-gold text-sm" />
                  <span className="text-brand-gold text-xs font-semibold">Verified Reviews</span>
                </div>
              </div>
            </div>
          </div>
        </RevealOnScroll>

        {/* Featured testimonial */}
        <RevealOnScroll>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-10 mb-6 relative overflow-hidden">
            {/* Large quote mark */}
            <div className="absolute top-4 right-8 font-display text-[120px] leading-none text-brand-gold/10 select-none pointer-events-none">
              &ldquo;
            </div>

            <div className="flex flex-col lg:flex-row items-start gap-7">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-gold rounded-2xl">
                  <span className="text-white font-bold text-xl">{testimonials[active].initials}</span>
                </div>
              </div>

              <div className="flex-1">
                {/* Rating + service */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <StarRating rating={testimonials[active].rating} size="md" />
                  <span className="text-xs font-bold text-brand-gold bg-brand-gold/10 border border-brand-gold/20 px-3 py-1 rounded-full uppercase tracking-wider">
                    {testimonials[active].service}
                  </span>
                </div>

                {/* Quote */}
                <blockquote className="text-white/90 text-lg leading-relaxed mb-5 font-display italic">
                  &ldquo;{testimonials[active].text}&rdquo;
                </blockquote>

                <div className="flex items-center gap-2">
                  <div>
                    <div className="text-white font-bold text-sm">{testimonials[active].name}</div>
                    <div className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                      <i className="ri-map-pin-line text-brand-gold text-xs" />
                      {testimonials[active].location}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </RevealOnScroll>

        {/* Reviewer grid */}
        <RevealOnScroll delay={100}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {testimonials.map((t, idx) => (
              <button
                key={t.id}
                onClick={() => setActive(idx)}
                className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${
                  active === idx
                    ? 'border-brand-gold bg-brand-gold/10 scale-[1.02]'
                    : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/8'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${active === idx ? 'bg-brand-gold' : 'bg-brand-gold/20'}`}>
                    <span className={`font-bold text-xs ${active === idx ? 'text-white' : 'text-brand-gold'}`}>{t.initials}</span>
                  </div>
                  <span className="text-white text-xs font-semibold truncate">{t.name}</span>
                </div>
                <div className="flex items-center gap-0.5 mb-1.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-brand-gold" style={{ fontSize: '9px' }} />
                  ))}
                </div>
                <div className="text-xs text-gray-500 truncate">{t.service}</div>
              </button>
            ))}
          </div>
        </RevealOnScroll>

        {/* Platform badges */}
        <RevealOnScroll delay={150}>
          <div className="flex flex-wrap items-center justify-center gap-6 pt-6 border-t border-white/10">
            <span className="text-xs text-gray-500">Verified 5-star reviews on:</span>
            {[
              { name: 'Google Reviews', icon: 'ri-google-fill' },
              { name: 'Yelp', icon: 'ri-yelp-line' },
              { name: 'HomeAdvisor', icon: 'ri-home-line' },
              { name: 'Angi', icon: 'ri-hammer-fill' },
            ].map((platform) => (
              <span key={platform.name} className="flex items-center gap-1.5 text-xs text-gray-400">
                <i className={`${platform.icon} text-brand-gold text-sm`} />
                {platform.name}
              </span>
            ))}
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}
